/**
 * CMS Admin Panel Component
 * ZO Bull - Admin interface for managing CMS content
 */
import React, { useState, useEffect } from 'react';
import './CMSAdmin.css';

interface Feature {
  id: number;
  feature_key: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  is_enabled: boolean;
  sort_order: number;
  is_new: boolean;
  is_beta: boolean;
}

interface PricingTier {
  id: number;
  tier_key: string;
  display_name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta_text: string;
  is_popular: boolean;
  is_active: boolean;
  is_trial: boolean;
  trial_days: number;
}

interface FAQ {
  id: number;
  question: string;
  answer: string;
  category: string;
  sort_order: number;
  is_published: boolean;
}

interface Page {
  id: number;
  slug: string;
  title: string;
  description?: string;
  content?: string;
  is_published: boolean;
  is_featured: boolean;
  updated_at: string;
}

type TabType = 'features' | 'pricing' | 'faqs' | 'pages';

export default function CMSAdmin() {
  const [activeTab, setActiveTab] = useState<TabType>('features');
  const [features, setFeatures] = useState<Feature[]>([]);
  const [pricing, setPricing] = useState<PricingTier[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingItem, setEditingItem] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
    'Content-Type': 'application/json',
  });

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'features') {
        const res = await fetch('/api/admin/cms/features', { headers: getAuthHeaders() });
        if (res.ok) setFeatures(await res.json());
      } else if (activeTab === 'pricing') {
        const res = await fetch('/api/admin/cms/pricing', { headers: getAuthHeaders() });
        if (res.ok) setPricing(await res.json());
      } else if (activeTab === 'faqs') {
        const res = await fetch('/api/admin/cms/faqs', { headers: getAuthHeaders() });
        if (res.ok) setFaqs(await res.json());
      } else if (activeTab === 'pages') {
        const res = await fetch('/api/admin/cms/pages', { headers: getAuthHeaders() });
        if (res.ok) setPages(await res.json());
      }
    } catch (err) {
      showMessage('error', 'Failed to load data');
    }
    setLoading(false);
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const saveFeature = async (feature: Feature) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/cms/features/${feature.feature_key}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(feature),
      });
      if (res.ok) {
        showMessage('success', 'Feature saved successfully');
        setEditingItem(null);
        loadData();
      } else {
        showMessage('error', 'Failed to save feature');
      }
    } catch (err) {
      showMessage('error', 'Failed to save feature');
    }
    setSaving(false);
  };

  const savePricing = async (tier: PricingTier) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/cms/pricing/${tier.tier_key}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(tier),
      });
      if (res.ok) {
        showMessage('success', 'Pricing tier saved');
        setEditingItem(null);
        loadData();
      } else {
        showMessage('error', 'Failed to save pricing');
      }
    } catch (err) {
      showMessage('error', 'Failed to save pricing');
    }
    setSaving(false);
  };

  const saveFaq = async (faq: Partial<FAQ>) => {
    setSaving(true);
    try {
      if (faq.id) {
        const res = await fetch(`/api/admin/cms/faqs/${faq.id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(faq),
        });
        if (res.ok) {
          showMessage('success', 'FAQ updated');
          setEditingItem(null);
          loadData();
        } else {
          showMessage('error', 'Failed to update FAQ');
        }
      } else {
        const res = await fetch('/api/admin/cms/faqs', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(faq),
        });
        if (res.ok) {
          showMessage('success', 'FAQ created');
          setEditingItem(null);
          loadData();
        } else {
          showMessage('error', 'Failed to create FAQ');
        }
      }
    } catch (err) {
      showMessage('error', 'Failed to save FAQ');
    }
    setSaving(false);
  };

  const deleteFaq = async (id: number) => {
    if (!confirm('Delete this FAQ?')) return;
    try {
      const res = await fetch(`/api/admin/cms/faqs/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        showMessage('success', 'FAQ deleted');
        loadData();
      } else {
        showMessage('error', 'Failed to delete FAQ');
      }
    } catch (err) {
      showMessage('error', 'Failed to delete FAQ');
    }
  };

  return (
    <div className="cms-admin">
      <div className="cms-admin-header">
        <h2>📝 CMS Content Management</h2>
        <p>Manage landing page features, pricing, and FAQs</p>
      </div>

      {message && (
        <div className={`cms-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="cms-tabs">
        <button
          className={activeTab === 'features' ? 'active' : ''}
          onClick={() => setActiveTab('features')}
        >
          ✨ Features
        </button>
        <button
          className={activeTab === 'pricing' ? 'active' : ''}
          onClick={() => setActiveTab('pricing')}
        >
          💰 Pricing
        </button>
        <button
          className={activeTab === 'faqs' ? 'active' : ''}
          onClick={() => setActiveTab('faqs')}
        >
          ❓ FAQs
        </button>
        <button
          className={activeTab === 'pages' ? 'active' : ''}
          onClick={() => setActiveTab('pages')}
        >
          📄 Pages
        </button>
      </div>

      <div className="cms-content">
        {loading ? (
          <div className="cms-loading">Loading...</div>
        ) : activeTab === 'features' ? (
          <FeaturesTab
            features={features}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
            onSave={saveFeature}
            saving={saving}
          />
        ) : activeTab === 'pricing' ? (
          <PricingTab
            pricing={pricing}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
            onSave={savePricing}
            saving={saving}
          />
        ) : activeTab === 'pages' ? (
          <PagesTab
            pages={pages}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
            saving={saving}
          />
        ) : (
          <FaqsTab
            faqs={faqs}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
            onSave={saveFaq}
            onDelete={deleteFaq}
            saving={saving}
          />
        )}
      </div>
    </div>
  );
}

// Features Tab Component
function FeaturesTab({ features, editingItem, setEditingItem, onSave, saving }: any) {
  const [form, setForm] = useState<Feature | null>(null);

  const startEdit = (feature: Feature) => {
    setForm({ ...feature });
    setEditingItem(feature.feature_key);
  };

  const handleSave = () => {
    if (form) onSave(form);
  };

  return (
    <div className="cms-section">
      <h3>Landing Page Features</h3>
      <p className="cms-help">Click on a feature to edit. Changes apply immediately to the landing page.</p>

      <div className="cms-grid features-grid">
        {features.map((f: Feature) => (
          <div
            key={f.feature_key}
            className={`cms-card feature-card ${editingItem === f.feature_key ? 'editing' : ''}`}
            onClick={() => startEdit(f)}
          >
            <div className="feature-header">
              <span className="feature-icon">{f.icon}</span>
              <div className="feature-badges">
                {f.is_new && <span className="badge new">NEW</span>}
                {f.is_beta && <span className="badge beta">BETA</span>}
                {!f.is_enabled && <span className="badge disabled">DISABLED</span>}
              </div>
            </div>
            <h4>{f.title}</h4>
            <p>{f.description}</p>
            <div className="feature-color" style={{ backgroundColor: f.color }} />
          </div>
        ))}
      </div>

      {form && editingItem === form.feature_key && (
        <div className="cms-modal-overlay" onClick={() => setEditingItem(null)}>
          <div className="cms-modal" onClick={e => e.stopPropagation()}>
            <h3>Edit Feature: {form.title}</h3>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Icon (emoji)</label>
                <input
                  type="text"
                  value={form.icon}
                  onChange={e => setForm({ ...form, icon: e.target.value })}
                  maxLength={10}
                />
              </div>
              <div className="form-group">
                <label>Color (hex)</label>
                <input
                  type="text"
                  value={form.color}
                  onChange={e => setForm({ ...form, color: e.target.value })}
                />
                <input
                  type="color"
                  value={form.color}
                  onChange={e => setForm({ ...form, color: e.target.value })}
                  className="color-picker"
                />
              </div>
            </div>
            <div className="form-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.is_enabled}
                  onChange={e => setForm({ ...form, is_enabled: e.target.checked })}
                />
                Enabled
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.is_new}
                  onChange={e => setForm({ ...form, is_new: e.target.checked })}
                />
                Show "NEW" badge
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.is_beta}
                  onChange={e => setForm({ ...form, is_beta: e.target.checked })}
                />
                Show "BETA" badge
              </label>
            </div>
            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setEditingItem(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Pricing Tab Component
function PricingTab({ pricing, editingItem, setEditingItem, onSave, saving }: any) {
  const [form, setForm] = useState<PricingTier | null>(null);

  const startEdit = (tier: PricingTier) => {
    setForm({ ...tier, features: [...(tier.features || [])] });
    setEditingItem(tier.tier_key);
  };

  const handleSave = () => {
    if (form) onSave(form);
  };

  const updateFeature = (index: number, value: string) => {
    const features = [...(form?.features || [])];
    features[index] = value;
    setForm({ ...form, features });
  };

  return (
    <div className="cms-section">
      <h3>Pricing Tiers</h3>
      <p className="cms-help">Edit pricing tiers displayed on the landing page.</p>

      <div className="pricing-cards">
        {pricing.map((p: PricingTier) => (
          <div
            key={p.tier_key}
            className={`pricing-card-admin ${p.is_popular ? 'popular' : ''}`}
          >
            {p.is_popular && <div className="popular-badge">Most Popular</div>}
            {p.is_trial && <div className="trial-badge">Trial</div>}
            <h4>{p.display_name}</h4>
            <div className="price">
              <span className="amount">{p.price}</span>
              <span className="period">{p.period}</span>
            </div>
            <ul>
              {(p.features || []).map((f, i) => (
                <li key={i}>✓ {f}</li>
              ))}
            </ul>
            <button className="btn-edit" onClick={() => startEdit(p)}>Edit</button>
          </div>
        ))}
      </div>

      {form && editingItem === form.tier_key && (
        <div className="cms-modal-overlay" onClick={() => setEditingItem(null)}>
          <div className="cms-modal" onClick={e => e.stopPropagation()}>
            <h3>Edit Pricing: {form.display_name}</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={form.display_name}
                  onChange={e => setForm({ ...form, display_name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Price</label>
                <input
                  type="text"
                  value={form.price}
                  onChange={e => setForm({ ...form, price: e.target.value })}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Period</label>
              <input
                type="text"
                value={form.period}
                onChange={e => setForm({ ...form, period: e.target.value })}
                placeholder="e.g., /month, 1 Month, Contact us"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="form-group">
              <label>Features (one per line)</label>
              <textarea
                value={(form.features || []).join('\n')}
                onChange={e => setForm({ ...form, features: e.target.value.split('\n') })}
                rows={6}
              />
            </div>
            <div className="form-group">
              <label>CTA Button Text</label>
              <input
                type="text"
                value={form.cta_text}
                onChange={e => setForm({ ...form, cta_text: e.target.value })}
              />
            </div>
            <div className="form-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.is_popular}
                  onChange={e => setForm({ ...form, is_popular: e.target.checked })}
                />
                Mark as Popular
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={e => setForm({ ...form, is_active: e.target.checked })}
                />
                Active (show on page)
              </label>
            </div>
            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setEditingItem(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// FAQs Tab Component
function FaqsTab({ faqs, editingItem, setEditingItem, onSave, onDelete, saving }: any) {
  const [form, setForm] = useState<Partial<FAQ> | null>(null);

  const startNew = () => {
    setForm({ question: '', answer: '', category: '', sort_order: 0, is_published: true });
    setEditingItem('new');
  };

  const startEdit = (faq: FAQ) => {
    setForm({ ...faq });
    setEditingItem(faq.id);
  };

  const handleSave = () => {
    onSave(form);
  };

  return (
    <div className="cms-section">
      <div className="section-header-row">
        <h3>Frequently Asked Questions</h3>
        <button className="btn-primary" onClick={startNew}>+ Add FAQ</button>
      </div>
      <p className="cms-help">Manage FAQ items shown on the pricing section.</p>

      <div className="faq-list">
        {faqs.map((f: FAQ) => (
          <div key={f.id} className="faq-item-admin">
            <div className="faq-content">
              <span className="faq-category">{f.category || 'General'}</span>
              <h4>{f.question}</h4>
              <p>{f.answer}</p>
            </div>
            <div className="faq-actions">
              {!f.is_published && <span className="badge draft">Draft</span>}
              <button className="btn-icon" onClick={() => startEdit(f)}>✏️</button>
              <button className="btn-icon delete" onClick={() => onDelete(f.id)}>🗑️</button>
            </div>
          </div>
        ))}
      </div>

      {form && editingItem === 'new' || (form && typeof form.id === 'number') ? (
        <div className="cms-modal-overlay" onClick={() => setEditingItem(null)}>
          <div className="cms-modal" onClick={e => e.stopPropagation()}>
            <h3>{form?.id ? 'Edit FAQ' : 'New FAQ'}</h3>
            <div className="form-group">
              <label>Question</label>
              <input
                type="text"
                value={form?.question || ''}
                onChange={e => setForm({ ...form, question: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Answer</label>
              <textarea
                value={form?.answer || ''}
                onChange={e => setForm({ ...form, answer: e.target.value })}
                rows={4}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <input
                  type="text"
                  value={form?.category || ''}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                  placeholder="e.g., Billing, Trading, Plans"
                />
              </div>
              <div className="form-group">
                <label>Sort Order</label>
                <input
                  type="number"
                  value={form?.sort_order || 0}
                  onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form?.is_published ?? true}
                onChange={e => setForm({ ...form, is_published: e.target.checked })}
              />
              Published (visible on page)
            </label>
            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setEditingItem(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save FAQ'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Pages Tab Component
function PagesTab({ pages, editingItem, setEditingItem, saving }: any) {
  const [form, setForm] = useState<Partial<Page>>({});
  const [savingLocal, setSavingLocal] = useState(false);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
    'Content-Type': 'application/json',
  });

  const startEdit = (page: Page) => {
    setForm({ ...page });
    setEditingItem(page.slug);
  };

  const handleSave = async () => {
    if (!form.slug) return;
    setSavingLocal(true);
    try {
      const res = await fetch(`/api/admin/cms/pages/${form.slug}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setEditingItem(null);
        window.location.reload();
      } else {
        alert('Failed to save page');
      }
    } catch (err) {
      alert('Failed to save page');
    }
    setSavingLocal(false);
  };

  return (
    <div className="cms-section">
      <h3>CMS Pages</h3>
      <p className="cms-help">Edit static pages like About, Contact, Terms, etc.</p>

      <div className="pages-list">
        {pages.map((p: Page) => (
          <div key={p.id} className="page-item-admin">
            <div className="page-info">
              <div className="page-badges">
                {!p.is_published && <span className="badge draft">Draft</span>}
                {p.is_featured && <span className="badge new">Featured</span>}
              </div>
              <h4>{p.title}</h4>
              <span className="page-slug">/{p.slug}</span>
              {p.description && <p className="page-desc">{p.description}</p>}
              <span className="page-updated">Updated: {new Date(p.updated_at).toLocaleDateString()}</span>
            </div>
            <button className="btn-edit" onClick={() => startEdit(p)}>Edit Content</button>
          </div>
        ))}
      </div>

      {form && editingItem && (
        <div className="cms-modal-overlay" onClick={() => setEditingItem(null)}>
          <div className="cms-modal cms-modal-wide" onClick={e => e.stopPropagation()}>
            <h3>Edit Page: {form.title}</h3>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={form.title || ''}
                onChange={e => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Description (meta)</label>
              <input
                type="text"
                value={form.description || ''}
                onChange={e => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Content (HTML allowed)</label>
              <textarea
                value={form.content || ''}
                onChange={e => setForm({ ...form, content: e.target.value })}
                rows={15}
                className="content-editor"
              />
            </div>
            <div className="form-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.is_published ?? false}
                  onChange={e => setForm({ ...form, is_published: e.target.checked })}
                />
                Published
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.is_featured ?? false}
                  onChange={e => setForm({ ...form, is_featured: e.target.checked })}
                />
                Featured
              </label>
            </div>
            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setEditingItem(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving || savingLocal}>
                {saving || savingLocal ? 'Saving...' : 'Save Page'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
